import { p256 } from "@noble/curves/nist.js"

/**
 * SPAKE2 over P-256 as in RFC 9382 with SHA-256, HKDF-SHA-256, and
 * HMAC-SHA-256. A short human code becomes the password; a wrong guess
 * derives an unrelated key, and nothing exchanged through the blind
 * rendezvous allows an offline search of the code.
 */

export type Spake2Role = "a" | "b"

export const SPAKE2_ELEMENT_BYTES = 65
export const SPAKE2_KEY_BYTES = 16

const M_COMPRESSED = "02886e2f97ace46e55ba9dd7242579f2993b64e16ef3dcab95afd497333d8fa12f"
const N_COMPRESSED = "03d8bbd6c639c62937b04d997f38c3770719c629d7014d49a24b4f98baa1292b49"
const CONFIRMATION_INFO = new TextEncoder().encode("ConfirmationKeys")

/** Bytes backed by a plain ArrayBuffer, which WebCrypto requires. */
export type OwnedBytes = Uint8Array<ArrayBuffer>

export interface Spake2Keys {
    ke: OwnedBytes
    ka: OwnedBytes
    kcA: OwnedBytes
    kcB: OwnedBytes
    transcript: OwnedBytes
}

export function ownedBytes(bytes: Uint8Array): OwnedBytes {
    return new Uint8Array(bytes)
}

export function hexToBytes(hex: string): Uint8Array {
    const out = new Uint8Array(hex.length / 2)
    for (let index = 0; index < out.length; index += 1) out[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16)
    return out
}

export function bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")
}

function bytesToBigInt(bytes: Uint8Array): bigint {
    let value = 0n
    for (const byte of bytes) value = (value << 8n) | BigInt(byte)
    return value
}

function bigIntToBytes(value: bigint, length: number): Uint8Array {
    const out = new Uint8Array(length)
    let remaining = value
    for (let index = length - 1; index >= 0; index -= 1) {
        out[index] = Number(remaining & 0xffn)
        remaining >>= 8n
    }
    return out
}

function lengthPrefixed(part: Uint8Array): Uint8Array {
    const out = new Uint8Array(8 + part.length)
    new DataView(out.buffer).setBigUint64(0, BigInt(part.length), true)
    out.set(part, 8)
    return out
}

export function concatBytes(...parts: Uint8Array[]): OwnedBytes {
    const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0))
    let offset = 0
    for (const part of parts) {
        out.set(part, offset)
        offset += part.length
    }
    return out
}

/** Derives w from a human code, bound to this product's exchanges. */
export async function passwordScalar(context: string, password: string): Promise<Uint8Array> {
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${context} ${password}`)))
    return bigIntToBytes(bytesToBigInt(digest) % p256.Point.Fn.ORDER, 32)
}

export function randomScalar(): Uint8Array {
    const order = p256.Point.Fn.ORDER
    for (;;) {
        const candidate = crypto.getRandomValues(new Uint8Array(32))
        const value = bytesToBigInt(candidate)
        if (value > 0n && value < order) return candidate
    }
}

function blindPoint(role: Spake2Role) {
    return p256.Point.fromBytes(hexToBytes(role === "a" ? M_COMPRESSED : N_COMPRESSED))
}

export class Spake2 {
    private readonly wScalar: bigint
    private readonly scalar: bigint
    private readonly element: Uint8Array
    private readonly w: Uint8Array

    constructor(
        readonly role: Spake2Role,
        w: Uint8Array,
        private readonly identityA: Uint8Array,
        private readonly identityB: Uint8Array,
        scalar: Uint8Array = randomScalar()
    ) {
        if (w.length !== 32 || scalar.length !== 32) throw new Error("SPAKE2 scalars must be 32 bytes")
        const order = p256.Point.Fn.ORDER
        this.wScalar = bytesToBigInt(w)
        this.scalar = bytesToBigInt(scalar)
        if (this.scalar <= 0n || this.scalar >= order || this.wScalar >= order) throw new Error("SPAKE2 scalar out of range")
        this.w = w.slice()
        const blind = this.wScalar === 0n ? p256.Point.ZERO : blindPoint(role).multiply(this.wScalar)
        this.element = p256.Point.BASE.multiply(this.scalar).add(blind).toBytes(false)
    }

    /** The public element to send to the peer. */
    get publicElement(): Uint8Array {
        return this.element.slice()
    }

    async finish(peerElement: Uint8Array): Promise<Spake2Keys> {
        if (peerElement.length !== SPAKE2_ELEMENT_BYTES) throw new Error("SPAKE2 peer element has the wrong size")
        const peer = p256.Point.fromBytes(peerElement)
        peer.assertValidity()
        const peerRole: Spake2Role = this.role === "a" ? "b" : "a"
        const peerBlind = this.wScalar === 0n ? p256.Point.ZERO : blindPoint(peerRole).multiply(this.wScalar)
        const shared = peer.subtract(peerBlind).multiply(this.scalar)
        if (shared.is0()) throw new Error("SPAKE2 shared point is the identity")
        const pA = this.role === "a" ? this.element : peerElement
        const pB = this.role === "a" ? peerElement : this.element
        const transcript = concatBytes(
            lengthPrefixed(this.identityA),
            lengthPrefixed(this.identityB),
            lengthPrefixed(pA),
            lengthPrefixed(pB),
            lengthPrefixed(shared.toBytes(false)),
            lengthPrefixed(this.w)
        )
        return deriveKeys(transcript)
    }
}

async function deriveKeys(transcript: OwnedBytes): Promise<Spake2Keys> {
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", transcript))
    const ke = digest.slice(0, SPAKE2_KEY_BYTES)
    const ka = digest.slice(SPAKE2_KEY_BYTES)
    const master = await crypto.subtle.importKey("raw", ownedBytes(ka), "HKDF", false, ["deriveBits"])
    const confirmationKeys = new Uint8Array(
        await crypto.subtle.deriveBits({ name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: CONFIRMATION_INFO }, master, 256)
    )
    return { ke, ka, kcA: confirmationKeys.slice(0, SPAKE2_KEY_BYTES), kcB: confirmationKeys.slice(SPAKE2_KEY_BYTES), transcript }
}

export async function confirmation(role: Spake2Role, keys: Spake2Keys): Promise<Uint8Array> {
    const key = await crypto.subtle.importKey("raw", ownedBytes(role === "a" ? keys.kcA : keys.kcB), { name: "HMAC", hash: "SHA-256" }, false, ["sign"])
    return new Uint8Array(await crypto.subtle.sign("HMAC", key, ownedBytes(keys.transcript)))
}

export async function verifyConfirmation(peerRole: Spake2Role, keys: Spake2Keys, value: Uint8Array): Promise<boolean> {
    const key = await crypto.subtle.importKey("raw", ownedBytes(peerRole === "a" ? keys.kcA : keys.kcB), { name: "HMAC", hash: "SHA-256" }, false, ["verify"])
    return crypto.subtle.verify("HMAC", key, ownedBytes(value), ownedBytes(keys.transcript))
}

/** AES-256-GCM under a key derived from Ke for one direction label. */
export async function sealingKey(keys: Spake2Keys, label: string): Promise<CryptoKey> {
    const master = await crypto.subtle.importKey("raw", ownedBytes(keys.ke), "HKDF", false, ["deriveBits"])
    const material = await crypto.subtle.deriveBits(
        { name: "HKDF", hash: "SHA-256", salt: ownedBytes(keys.transcript.slice(0, 32)), info: new TextEncoder().encode(label) },
        master,
        256
    )
    return crypto.subtle.importKey("raw", material, { name: "AES-GCM" }, false, ["encrypt", "decrypt"])
}

export async function seal(key: CryptoKey, plaintext: Uint8Array): Promise<OwnedBytes> {
    const nonce = crypto.getRandomValues(new Uint8Array(12))
    const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, key, ownedBytes(plaintext)))
    return concatBytes(nonce, ciphertext)
}

export async function open(key: CryptoKey, sealed: Uint8Array): Promise<OwnedBytes> {
    if (sealed.length < 12 + 16) throw new Error("Sealed message is too short")
    const owned = ownedBytes(sealed)
    return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: owned.slice(0, 12) }, key, owned.slice(12)))
}
